import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useI18nFormatters } from 'hooks/i18nFormatters';

import { Col, Collapse, Divider, Row } from 'antd';

import CircleIcon from 'components/Icon/CircleIcon';

import './styles.less';

const { Panel } = Collapse;
const I18N_BASE_PATH = `pages.private.profiles.components.tabs.components.basicInfos.components.person.addressContent.components.addressItem.index`;

const AddressItem = ({ data: address }) => {
  const { t: translate } = useTranslation();
  const { i18nFormatDate } = useI18nFormatters();

  const {
    addressMain,
    number,
    complement,
    neighborhood,
    city,
    state,
    zipCode,
    type,
    householdCode,
    buildingCode,
    lastUpdateDate,
    hasOptIn,
    householdMonthAveragePassages,
    householdNumberOfEntities,
    buildingMonthAveragePassages,
    buildingNumberOfHouseholds,
    priority,
    isMain,
    isRatified,
    firstPassageDate,
    lastPassageDate,
    creationDate,
    householdTotalPassages,
    householdValidationPassages,
    householdQueryPassages,
    householdCrawlingPassages,
    householdBadPassages,
    buildingTotalPassages,
    buildingValidationPassages,
    buildingQueryPassages,
    buildingCrawlingPassages,
    buildingBadPassages,
    last3MonthsPassages,
    last6MonthsPassages,
    last12MonthsPassages,
    last18MonthsPassages,
  } = address || {};

  const addressType = useMemo(() => {
    return translate(
      `pages.private.profiles.components.tabs.components.basicInfos.components.person.addressContent.components.addressItem.index.addressType.${
        // eslint-disable-next-line no-nested-ternary
        type === 'HOME' ? 'home' : type === 'WORK' ? 'work' : 'other'
      }`
    );
  }, [type, translate]);

  const addressIconType = useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    return type === 'HOME' ? 'home' : type === 'WORK' ? 'company' : 'map_pin';
  }, [type]);

  return (
    <div>
      <div id="address-item-execution-container">
        <CircleIcon icon={`caf-ic_${addressIconType}`} />
        <section className="flex space-between address-item-execution-information">
          <span>
            {addressMain && `${addressMain},`} {number && `${number},`}{' '}
            {complement && neighborhood ? `${complement} -` : `${complement || ''}`}{' '}
            {neighborhood || ''} <br />
            {city && state ? `${city} /` : `${city || ''}`} {state && `${state},`}{' '}
            {zipCode || ''}
          </span>
          <div className="mrg-top-5 address-item-type-execution-tag">
            <small>{addressType}</small>
          </div>
        </section>
      </div>
      {(buildingCode || householdCode) && (
        <div id="more-details">
          <Row>
            <Col
              span={24}
              className="col-ant"
              style={{ marginLeft: '0px', paddingLeft: '0px' }}
            >
              <Collapse expandIconPosition="right" ghost>
                <Panel
                  header={<b>{translate(`${I18N_BASE_PATH}.fields.moreDetails`)}</b>}
                  key="1"
                  showArrow
                  className="mrg-btm-20"
                >
                  <Divider />
                  <Row>
                    {complement && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.complement`)}
                        </strong>
                        <span>{complement}</span>
                      </Col>
                    )}
                    {householdCode && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.householdCode`)}
                        </strong>
                        <span>{householdCode}</span>
                      </Col>
                    )}
                    {buildingCode && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.buildingCode`)}
                        </strong>
                        <span>{buildingCode}</span>
                      </Col>
                    )}
                    {lastUpdateDate && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.lastUpdateDate`)}
                        </strong>
                        <span>{i18nFormatDate(lastUpdateDate, 1)}</span>
                      </Col>
                    )}
                    {hasOptIn && hasOptIn !== null && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>{translate(`${I18N_BASE_PATH}.fields.hasOptIn`)}</strong>
                        <span>{hasOptIn ? 'True' : 'False'}</span>
                      </Col>
                    )}
                    {householdMonthAveragePassages && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(
                            `${I18N_BASE_PATH}.fields.householdMonthAveragePassages`
                          )}
                        </strong>
                        <span>{householdMonthAveragePassages}</span>
                      </Col>
                    )}
                    {householdNumberOfEntities && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(
                            `${I18N_BASE_PATH}.fields.householdNumberOfEntities`
                          )}
                        </strong>
                        <span>{householdNumberOfEntities}</span>
                      </Col>
                    )}
                    {buildingMonthAveragePassages && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(
                            `${I18N_BASE_PATH}.fields.buildingMonthAveragePassages`
                          )}
                        </strong>
                        <span>{buildingMonthAveragePassages}</span>
                      </Col>
                    )}
                    {buildingNumberOfHouseholds && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(
                            `${I18N_BASE_PATH}.fields.buildingNumberOfHouseholds`
                          )}
                        </strong>
                        <span>{buildingNumberOfHouseholds}</span>
                      </Col>
                    )}
                    {priority && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>{translate(`${I18N_BASE_PATH}.fields.priority`)}</strong>
                        <span>{priority}</span>
                      </Col>
                    )}
                    {isMain && isMain !== null && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>{translate(`${I18N_BASE_PATH}.fields.isMain`)}</strong>
                        <span>{isMain ? 'True' : 'False'}</span>
                      </Col>
                    )}
                    {isRatified && isRatified !== null && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.isRatified`)}
                        </strong>
                        <span>{isRatified ? 'True' : 'False'}</span>
                      </Col>
                    )}
                    {firstPassageDate && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.firstPassageDate`)}
                        </strong>
                        <span>{i18nFormatDate(firstPassageDate, 1)}</span>
                      </Col>
                    )}
                    {lastPassageDate && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.lastPassageDate`)}
                        </strong>
                        <span>{i18nFormatDate(lastPassageDate, 1)}</span>
                      </Col>
                    )}
                    {creationDate && (
                      <Col className="col-ant mrg-left-10 mrg-btm-15" span={24}>
                        <strong>
                          {translate(`${I18N_BASE_PATH}.fields.creationDate`)}
                        </strong>
                        <span>{i18nFormatDate(creationDate, 1)}</span>
                      </Col>
                    )}
                    {(householdTotalPassages ||
                      householdValidationPassages ||
                      householdQueryPassages) && (
                      <Col
                        span={24}
                        className="col-ant"
                        style={{ marginLeft: '0px', paddingLeft: '0px' }}
                      >
                        <Collapse expandIconPosition="right" ghost>
                          <Panel
                            header={
                              <b>{translate(`${I18N_BASE_PATH}.fields.housePassages`)}</b>
                            }
                            key="2"
                            showArrow
                            className="mrg-top-20"
                          >
                            <Row>
                              {householdTotalPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.householdTotalPassages`
                                    )}
                                  </strong>
                                  <span>{householdTotalPassages}</span>
                                </Col>
                              )}
                              {householdValidationPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.householdValidationPassages`
                                    )}
                                  </strong>
                                  <span>{householdValidationPassages}</span>
                                </Col>
                              )}
                              {householdQueryPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.householdQueryPassages`
                                    )}
                                  </strong>
                                  <span>{householdQueryPassages}</span>
                                </Col>
                              )}
                            </Row>
                          </Panel>
                        </Collapse>
                        <Divider />
                      </Col>
                    )}
                    {(householdCrawlingPassages || householdBadPassages) && (
                      <Col
                        span={24}
                        className="col-ant"
                        style={{ marginLeft: '0px', paddingLeft: '0px' }}
                      >
                        <Collapse expandIconPosition="right" ghost>
                          <Panel
                            header={
                              <b>{translate(`${I18N_BASE_PATH}.fields.houseCode`)}</b>
                            }
                            key="2"
                            showArrow
                            className="mrg-top-20"
                          >
                            <Row>
                              {householdCrawlingPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.householdCrawlingPassages`
                                    )}
                                  </strong>
                                  <span>{householdCrawlingPassages}</span>
                                </Col>
                              )}
                              {householdBadPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.householdBadPassages`
                                    )}
                                  </strong>
                                  <span>{householdBadPassages}</span>
                                </Col>
                              )}
                            </Row>
                          </Panel>
                        </Collapse>
                        <Divider />
                      </Col>
                    )}
                    {(buildingTotalPassages ||
                      buildingValidationPassages ||
                      buildingQueryPassages) && (
                      <Col
                        span={24}
                        className="col-ant"
                        style={{ marginLeft: '0px', paddingLeft: '0px' }}
                      >
                        <Collapse expandIconPosition="right" ghost>
                          <Panel
                            header={
                              <b>{translate(`${I18N_BASE_PATH}.fields.buildPassages`)}</b>
                            }
                            key="2"
                            showArrow
                            className="mrg-top-20"
                          >
                            <Row>
                              {buildingTotalPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.buildingTotalPassages`
                                    )}
                                  </strong>
                                  <span>{buildingTotalPassages}</span>
                                </Col>
                              )}
                              {buildingValidationPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.buildingValidationPassages`
                                    )}
                                  </strong>
                                  <span>{buildingValidationPassages}</span>
                                </Col>
                              )}
                              {buildingQueryPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.buildingQueryPassages`
                                    )}
                                  </strong>
                                  <span>{buildingQueryPassages}</span>
                                </Col>
                              )}
                            </Row>
                          </Panel>
                        </Collapse>
                        <Divider />
                      </Col>
                    )}
                    {(buildingCrawlingPassages || buildingBadPassages) && (
                      <Col
                        span={24}
                        className="col-ant"
                        style={{ marginLeft: '0px', paddingLeft: '0px' }}
                      >
                        <Collapse expandIconPosition="right" ghost>
                          <Panel
                            header={
                              <b>{translate(`${I18N_BASE_PATH}.fields.buildCode`)}</b>
                            }
                            key="2"
                            showArrow
                            className="mrg-top-20"
                          >
                            <Row>
                              {buildingCrawlingPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.buildingCrawlingPassages`
                                    )}
                                  </strong>
                                  <span>{buildingCrawlingPassages || '-'}</span>
                                </Col>
                              )}
                              {buildingBadPassages && (
                                <Col className="col-ant mrg-btm-15" span={8}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.buildingBadPassages`
                                    )}
                                  </strong>
                                  <span>{buildingBadPassages || '-'}</span>
                                </Col>
                              )}
                            </Row>
                          </Panel>
                        </Collapse>
                        <Divider />
                      </Col>
                    )}
                    {(last3MonthsPassages ||
                      last6MonthsPassages ||
                      last12MonthsPassages ||
                      last18MonthsPassages) && (
                      <Col
                        span={24}
                        className="col-ant"
                        style={{ marginLeft: '0px', paddingLeft: '0px' }}
                      >
                        <Collapse expandIconPosition="right" ghost>
                          <Panel
                            header={
                              <b>{translate(`${I18N_BASE_PATH}.fields.passages`)}</b>
                            }
                            key="2"
                            showArrow
                            className="mrg-top-20"
                          >
                            <Row>
                              {last3MonthsPassages && (
                                <Col className="col-ant mrg-btm-15" span={24}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.last3MonthsPassages`
                                    )}
                                  </strong>
                                  <span>{last3MonthsPassages || '-'}</span>
                                </Col>
                              )}
                              {last6MonthsPassages && (
                                <Col className="col-ant mrg-btm-15" span={24}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.last6MonthsPassages`
                                    )}
                                  </strong>
                                  <span>{last6MonthsPassages || '-'}</span>
                                </Col>
                              )}
                              {last12MonthsPassages && (
                                <Col className="col-ant mrg-btm-15" span={24}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.last12MonthsPassages`
                                    )}
                                  </strong>
                                  <span>{last12MonthsPassages || '-'}</span>
                                </Col>
                              )}
                              {last18MonthsPassages && (
                                <Col className="col-ant mrg-btm-15" span={24}>
                                  <strong>
                                    {translate(
                                      `${I18N_BASE_PATH}.fields.last18MonthsPassages`
                                    )}
                                  </strong>
                                  <span>{last18MonthsPassages || '-'}</span>
                                </Col>
                              )}
                            </Row>
                          </Panel>
                        </Collapse>
                        <Divider />
                      </Col>
                    )}
                  </Row>
                </Panel>
              </Collapse>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

AddressItem.propTypes = {
  data: PropTypes.shape({
    addressMain: PropTypes.string,
    number: PropTypes.string,
    complement: PropTypes.string,
    neighborhood: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
};

export default AddressItem;
